import { Component, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, find, forEach, includes, map } from 'lodash';
import { Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { TValidPdfReportKeys, TValidPdfReportNames } from 'Src/ng2/shared/constants/pdf-reports.constant';
import { BatchEditService } from 'Src/ng2/shared/services/batch-edit-service/batch-edit-service';
import { ISchool } from 'Src/ng2/shared/typings/interfaces/school.interface';
import { IStudentPath } from 'src/ng2/shared/typings/interfaces/studentPaths.interface';
import {
  BulkUpdateStudentSupports,
  getBatchActionsSelectedStudentIds,
  getSchool,
  getStudentPathsEntitiesList,
} from '../../../store';
import { ToggleBatchActions, UpdateSelectedStudentIds } from '../../../store/actions/batch-actions-actions';
import { StudentSupportStatuses } from '../../constants/student-support-statuses.constant';
import { unsubscribeComponent } from '../../helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { IAssignSupportModalComponentData } from '../../modals/assign-support/assign-support-modal.component';
import { AssignSupportModalService } from '../../modals/assign-support/assign-support-modal.service';
import { IBaseModalData, ModalsService } from '../../modals/modals.service';
import { IRemoveStudentActivitiesModalComponentData } from '../../modals/student-activities/remove-student-activity/remove-student-activity-modal.component';
import { IStudentActivitiesModalComponentData } from '../../modals/student-activities/student-activities-modal.component';
import { IUser } from '../../typings/interfaces/user.interface';
import { TValidStudentPathStatuses } from '../../typings/types/student-path.types';
import {
  BulkCreateStudentPath,
  BulkUpdateStudentPath,
  LoadAllStudentPaths,
} from './../../../store/actions/student-paths-actions';
import { getAllStudentPathsLoadedStatus } from './../../../store/selectors/student-paths-selector';
import { IStudentPathsModalComponentData } from './../../modals/student-paths/student-paths-modal.component';
import { IBatchEditStudentParams } from './../../services/batch-edit-service/batch-edit-service';
import { ImSchool } from './../../services/im-models/im-school';

interface IBatchActionLink {
  label: string;
  tooltip: {
    default: string;
    unique: string;
  };
  tooltipStyle: string;
  key: string;
  userCanEdit: boolean;
  iconStyle: string;
  type: string;
  menuOptions?: any;
  hsOnly: boolean;
}
/* istanbul ignore next */
@Component({
  selector: 'batch-actions-menu',
  template: require('./batch-actions-menu.html'),
  styles: [require('./batch-actions-menu.scss')],
  encapsulation: ViewEncapsulation.None,
  providers: [BatchEditService],
})
@unsubscribeComponent
export class BatchActionsMenuComponent implements OnInit {
  @Input() userCanEdit: boolean;
  @Input() school: ISchool;
  @Input() currentUser: IUser;

  public studentIds: string[];
  public countStudents: number = null;
  public schoolId: string;
  public studentPaths: IStudentPath[];
  public batchEditMode: boolean;

  public openBatchEditModalSubscription: ISubscription;
  public selectedStudentIdsSubscription: ISubscription;
  public studentPathsLoadedSubscription: ISubscription;
  public studentPathsSubscription: ISubscription;
  public schoolSubscription: ISubscription;
  public openReportsModalSubscription: ISubscription;
  public openAssignSupportsModalSubscription: ISubscription;
  public openAddCollegeModalSubscription: ISubscription;
  public openUpdateCollegeModalSubscription: ISubscription;
  public updateStudentSupportModalSubscription: ISubscription;
  public studentActivitiesModalSubscription: ISubscription;
  public activitiesModalSubscription: ISubscription;

  public selectedReport: TValidPdfReportKeys;
  public selectedReportName: TValidPdfReportNames;

  public tooltipDefault: string = 'Select one or more students from the list';
  public toolTipDefaultStyle: string = 'batch-action-tooltip';
  public iconStyle: string;
  public batchActions: IBatchActionLink[];
  public destroy$: Subject<boolean> = new Subject<boolean>();
  public tooltipShowDelay: number = 500;
  public tooltipMenuItemShowDelay: number = 350;

  readonly BATCH_ACTION_LABEL = {
    ASSIGN_SUPPORT: 'ASSIGN_SUPPORT',
    COMPLETE_SUPPORT: 'COMPLETE_SUPPORT',
    REMOVE_SUPPORT: 'REMOVE_SUPPORT',
    ADD_COLLEGE: 'ADD_COLLEGE',
    UPDATE_STATUS: 'UPDATE_STATUS',
    REMOVE_COLLEGE: 'REMOVE_COLLEGE',
    ASSIGN_UPDATE_ACTIVITY: 'ASSIGN_UPDATE_ACTIVITY',
    REMOVE_ACTIVITY: 'REMOVE_ACTIVITY',
  };

  constructor(
    public modalsService: ModalsService,
    private store: Store<any>,
    private assignSupportModalService: AssignSupportModalService,
    private batchEditService: BatchEditService,
    private imSchool: ImSchool,
    @Inject('sdcActions') private sdcActions,
  ) {}

  ngOnInit() {
    const slice$ = this.store.select(getBatchActionsSelectedStudentIds);
    this.selectedStudentIdsSubscription = slice$.pipe(takeUntil(this.destroy$)).subscribe(studentIds => {
      this.studentIds = studentIds;
      this.countStudents = this.studentIds.length;
    });

    this.schoolSubscription = this.store
      .select(getSchool)
      .pipe(take(1))
      .subscribe(({ _id }) => (this.schoolId = _id));

    this.batchActions = this.getBatchActions();
  }

  getBatchActions(): IBatchActionLink[] {
    const batchActions = [
      {
        label: 'Supports',
        tooltip: {
          default: this.tooltipDefault,
          unique: 'Modify the support groups of the selected students',
        },
        tooltipStyle: this.toolTipDefaultStyle,
        key: 'SUPPORTS',
        userCanEdit: this.userCanEdit,
        iconStyle: 'people',
        type: 'MENU',
        menuOptions: [
          {
            key: this.BATCH_ACTION_LABEL.ASSIGN_SUPPORT,
            label: 'Assign support',
          },
          {
            key: this.BATCH_ACTION_LABEL.COMPLETE_SUPPORT,
            label: 'Mark complete',
          },
          {
            key: this.BATCH_ACTION_LABEL.REMOVE_SUPPORT,
            label: 'Remove from support',
          },
        ],
        hsOnly: false,
      },
      {
        label: 'Activities',
        tooltip: {
          default: this.tooltipDefault,
          unique: null,
        },
        tooltipStyle: this.toolTipDefaultStyle,
        key: 'ACTIVITIES',
        userCanEdit: this.userCanEdit,
        iconStyle: 'briefcase',
        type: 'MENU',
        menuOptions: [
          {
            key: this.BATCH_ACTION_LABEL.ASSIGN_UPDATE_ACTIVITY,
            label: 'Assign or update',
          },
          {
            key: this.BATCH_ACTION_LABEL.REMOVE_ACTIVITY,
            label: 'Remove activity',
          },
        ],
        hsOnly: true,
      },
      // {
      //   label: 'Flags',
      //   tooltip:  {
      //     default: this.tooltipDefault,
      //     unique: null
      //   },
      //   tooltipStyle: this.toolTipDefaultStyle,
      //   key: 'FLAGS',
      //   userCanEdit: null,
      //   iconStyle: 'batch-action-flag-icon',

      // },
      {
        label: 'College list',
        tooltip: {
          default: this.tooltipDefault,
          unique: 'Modify the college list for the selected students',
        },
        tooltipStyle: this.toolTipDefaultStyle,
        key: 'COLLEGE_LIST',
        userCanEdit: this.userCanEdit,
        iconStyle: 'college',
        type: 'MENU',
        menuOptions: [
          {
            key: this.BATCH_ACTION_LABEL.ADD_COLLEGE,
            label: 'Add college',
          },
          {
            key: this.BATCH_ACTION_LABEL.UPDATE_STATUS,
            label: 'Update status',
          },
          {
            key: this.BATCH_ACTION_LABEL.REMOVE_COLLEGE,
            label: 'Remove college',
          },
        ],
        hsOnly: true,
      },
      {
        label: 'Edit fields',
        tooltip: {
          default: this.tooltipDefault,
          unique: 'Edit data fields for the selected students',
        },
        tooltipStyle: this.toolTipDefaultStyle,
        key: 'FIELDS',
        userCanEdit: this.userCanEdit,
        iconStyle: 'edit',
        type: 'MODAL',
        hsOnly: false,
      },
      // {
      //   label: 'Add note',
      //   tooltip:  {
      //     default: this.tooltipDefault,
      //     unique: null
      //   },
      //   tooltipStyle: this.toolTipDefaultStyle,
      //   key: 'NOTES',
      //   userCanEdit: null,
      //   iconStyle: 'batch-action-note-icon',
      // },
      {
        label: 'Generate report',
        tooltip: {
          default: this.tooltipDefault,
          unique: 'Generate PDF report for the selected students',
        },
        tooltipStyle: this.toolTipDefaultStyle,
        key: 'REPORTS',
        userCanEdit: true,
        iconStyle: 'report',
        type: 'MODAL',
        hsOnly: false,
      },
    ];
    const isHs = this.imSchool.isHighSchool(this.school);
    const filteredBatchActions = filter(batchActions, (batchAction: IBatchActionLink) => {
      if (!isHs) return !batchAction.hsOnly;
      else return true;
    });
    return filteredBatchActions;
  }

  openAction(actionKey: string): void {
    switch (actionKey) {
      case this.BATCH_ACTION_LABEL.ASSIGN_SUPPORT:
        this.sendToAssignSupportModal();
        break;
      case this.BATCH_ACTION_LABEL.COMPLETE_SUPPORT:
        this.sendToUpdateStudentSupportModal(StudentSupportStatuses.backend.COMPLETED);
        break;
      case this.BATCH_ACTION_LABEL.REMOVE_SUPPORT:
        this.sendToUpdateStudentSupportModal(StudentSupportStatuses.backend.DELETED);
        break;
      // case 'FLAGS': // trigger dropdown
      //   return null;
      case this.BATCH_ACTION_LABEL.ADD_COLLEGE:
        this.sendToAddCollegeModal();
        break;
      case this.BATCH_ACTION_LABEL.REMOVE_COLLEGE:
        this.sendToRemoveCollegeModal();
        break;
      case this.BATCH_ACTION_LABEL.UPDATE_STATUS:
        this.sendToUpdateCollegeModal();
        break;
      case 'FIELDS':
        this.sendToBatchEditModal();
        break;
      // case 'NOTES':
      //   return null;
      case 'REPORTS':
        this.sendToStudentReportModal();
        break;
      case this.BATCH_ACTION_LABEL.ASSIGN_UPDATE_ACTIVITY:
        this.sendToStudentActivitiesModal('BATCH_ASSIGN_UPDATE');
        break;
      case this.BATCH_ACTION_LABEL.REMOVE_ACTIVITY:
        this.sendToRemoveStudentActivitiesModal();
        break;
      default:
        return null;
    }
  }

  cancel(): void {
    this.batchEditMode = false;
    this.store.dispatch(new ToggleBatchActions(this.batchEditMode));
    this.store.dispatch(new UpdateSelectedStudentIds([]));
  }

  /**
   *  TODO: Current design indicates batch actions will trigger 11 modals
   *  Not ideal to have 11 separate modal methods in this component, how can we dry this up?
   */
  sendToAssignSupportModal(): void {
    const data: IAssignSupportModalComponentData = {
      schoolId: this.schoolId,
      studentIds: this.studentIds,
      isProfileMode: false,
    };
    this.openAssignSupportsModalSubscription = this.modalsService
      .openAssignSupportModal(data)
      .afterClosed()
      .subscribe((data: any) => {
        // TODO: THIS IS NOT IDEAL - WE SHOULD NOT BE RECREATING STUDENT _IDS ON THE FRONTEND
        const _ids = map(this.studentIds, id => `${id}${this.schoolId}`);
        if (data) {
          const payload = {
            support: data,
            studentIds: _ids,
          };
          this.assignSupportModalService.bulkAssignStudentSupport(payload);
        }
      });
  }

  sendToUpdateStudentSupportModal(status: string): void {
    const data: IAssignSupportModalComponentData = {
      schoolId: this.schoolId,
      studentIds: this.studentIds,
      isProfileMode: false,
      isEditMode: true,
      status,
    };

    this.updateStudentSupportModalSubscription = this.modalsService
      .openAssignSupportModal(data)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          const payload = {
            studentSupportIds: res.studentSupportIds,
            status,
          };
          this.store.dispatch(new BulkUpdateStudentSupports(payload));
        }
      });
  }

  sendToBatchEditModal(): void {
    const baseModalData: IBaseModalData = {
      studentIds: this.studentIds,
      isProfileMode: false,
    };
    this.openBatchEditModalSubscription = this.modalsService
      .openBatchEditModal(baseModalData)
      .afterClosed()
      .pipe(
        tap((res: IBatchEditStudentParams['patch']) => {
          const studentIds = map(this.studentIds, id => `${id}${this.schoolId}`);
          const params = {
            studentIds,
            schoolId: this.schoolId,
            patch: res,
            destroy$: this.destroy$,
          };
          if (res) this.batchEditService.batchEditStudents(params);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  sendToStudentReportModal(): void {
    // TODO: THIS IS NOT IDEAL - WE SHOULD NOT BE RECREATING STUDENT _IDS ON THE FRONTEND
    const _ids = map(this.studentIds, id => `${id}${this.schoolId}`);
    const data = { studentIds: _ids, isProfileMode: false };
    this.openReportsModalSubscription = this.modalsService
      .openStudentReportModal(data)
      .afterClosed()
      .subscribe(data => {
        if (data) {
          this.batchEditMode = true;
          this.store.dispatch(new ToggleBatchActions(this.batchEditMode));
        }
      });
  }

  sendToAddCollegeModal(): void {
    const data: IStudentPathsModalComponentData = {
      schoolId: this.schoolId,
      studentIds: this.studentIds,
      isProfileMode: false,
      mode: 'ADD',
    };
    this.openAddCollegeModalSubscription = this.modalsService
      .openStudentPathsModal(data)
      .afterClosed()
      .subscribe((data: any) => {
        if (data) {
          const _ids = map(this.studentIds, id => `${id}${this.schoolId}`);
          const payload = {
            schoolId: this.schoolId,
            studentIds: _ids,
            pathId: data.pathId,
          };
          // dispatch action to bulk create student paths
          this.store.dispatch(new BulkCreateStudentPath(payload));
        }
      });
  }

  sendToRemoveCollegeModal(): void {
    // make sure studentPaths are loaded on the store
    this.store
      .select(getAllStudentPathsLoadedStatus)
      .pipe(take(1))
      .subscribe(allLoaded => {
        if (!allLoaded) this.store.dispatch(new LoadAllStudentPaths({ schoolId: this.schoolId }));
      });

    const data: IStudentPathsModalComponentData = {
      schoolId: this.schoolId,
      studentIds: this.studentIds,
      isProfileMode: false,
      mode: 'REMOVE',
    };
    const _ids = map(this.studentIds, id => `${id}${this.schoolId}`);

    this.openUpdateCollegeModalSubscription = this.modalsService
      .openStudentPathsModal(data)
      .afterClosed()
      .subscribe((data: { pathId: string }) => {
        if (data) {
          this.studentPathsSubscription = this.store
            .select(getStudentPathsEntitiesList)
            .pipe(take(1))
            .subscribe(studentPaths => {
              // only keep studentPaths for currently selected students (JE)
              this.studentPaths = filter(studentPaths, (studentPath: IStudentPath) => {
                return includes(_ids, studentPath.studentId);
              });
            });

          const { pathId } = data;
          // find studentPathIds - if no studentPath exists for a student/pathId, no patch will be sent for that student (JE)
          const matchedStudentPathIds: string[] = map(_ids, _id => {
            const matchedPath = find(this.studentPaths, studentPath => {
              return studentPath.path.pathId === pathId && studentPath.studentId === _id;
            });
            return matchedPath ? matchedPath._id : null;
          });

          const studentPathIds = filter(matchedStudentPathIds, id => !!id);
          if (studentPathIds.length) {
            const payload = {
              schoolId: this.schoolId,
              studentPathIds,
              status: 'DELETED' as TValidStudentPathStatuses,
            };
            // dispatch action to bulk remove student paths
            this.store.dispatch(new BulkUpdateStudentPath(payload));
          }
        }
      });
  }

  sendToUpdateCollegeModal(): void {
    // make sure studentPaths are loaded on the store
    this.store
      .select(getAllStudentPathsLoadedStatus)
      .pipe(take(1))
      .subscribe(allLoaded => {
        if (!allLoaded) this.store.dispatch(new LoadAllStudentPaths({ schoolId: this.schoolId }));
      });
    const data: IStudentPathsModalComponentData = {
      schoolId: this.schoolId,
      studentIds: this.studentIds,
      isProfileMode: false,
      mode: 'UPDATE',
    };
    const _ids = map(this.studentIds, id => `${id}${this.schoolId}`);

    this.openUpdateCollegeModalSubscription = this.modalsService
      .openStudentPathsModal(data)
      .afterClosed()
      .subscribe((data: { pathId: string; status: TValidStudentPathStatuses }) => {
        if (data) {
          this.studentPathsSubscription = this.store
            .select(getStudentPathsEntitiesList)
            .pipe(take(1))
            .subscribe(studentPaths => {
              // only keep studentPaths for currently selected students (JE)
              this.studentPaths = filter(studentPaths, (studentPath: IStudentPath) => {
                return includes(_ids, studentPath.studentId);
              });
            });

          this._handleUpdateCollege(data, _ids);
        }
      });
  }

  _handleUpdateCollege(data: { pathId: string; status: TValidStudentPathStatuses }, _ids: string[]): void {
    const { pathId, status } = data;
    // find studentPaths that already exist for selected students
    const studentPathIds = [];
    const studentIdsWithoutPath = [];
    forEach(_ids, id => {
      const matchedPath = find(this.studentPaths, (studentPath: IStudentPath) => {
        return studentPath.path.pathId === pathId && studentPath.studentId === id;
      });
      // if matchedPath.status === selected status, don't patch that student
      if (matchedPath && matchedPath.status !== status) studentPathIds.push(matchedPath._id);
      else if (!matchedPath) studentIdsWithoutPath.push(id);
    });

    if (status === 'DELETED' && studentPathIds.length) {
      const payload = {
        schoolId: this.schoolId,
        studentPathIds,
        status,
      };
      // dispatch action to bulk remove student paths
      this.store.dispatch(new BulkUpdateStudentPath(payload));
    } else {
      // payload includes studentIds for students who do not already have matching studentPath
      const payload = {
        schoolId: this.schoolId,
        studentPathIds,
        status,
        studentIds: null,
        pathId,
      };
      if (studentIdsWithoutPath.length) payload.studentIds = studentIdsWithoutPath;

      // only dispatch update if some students do not already have this path and status (JE)
      if (studentIdsWithoutPath.length || studentPathIds.length)
        this.store.dispatch(new BulkUpdateStudentPath(payload));
    }
  }

  sendToStudentActivitiesModal(mode: string): void {
    const { schoolYear } = find(this.school.termInfo, term => term.currentTermInd);
    const _ids = map(this.studentIds, id => `${id}${this.schoolId}`);
    const data: IStudentActivitiesModalComponentData = {
      currentSchoolYear: schoolYear,
      schoolId: this.schoolId,
      studentIds: _ids,
      mode,
      isProfileMode: false,
      currentUser: this.currentUser,
    };
    this.studentActivitiesModalSubscription = this.modalsService
      .openStudentActivitiesModal(data)
      .afterClosed()
      .subscribe((data: any) => {
        // allows user to create new activity and then return to the student activities modal
        if (data && data.returnTo === 'StudentActivitiesModal') {
          this.modalsService
            .openActivitiesModal({
              mode: 'CREATE',
              schoolId: this.schoolId,
              isProfileMode: true,
            })
            .afterClosed()
            .subscribe(payload => {
              if (data && data.returnTo === 'StudentActivitiesModal') {
                this.sendToStudentActivitiesModal(mode);
              }
            });
        }
      });
  }

  sendToRemoveStudentActivitiesModal(): void {
    const _ids = map(this.studentIds, id => `${id}${this.schoolId}`);
    const data: IRemoveStudentActivitiesModalComponentData = {
      schoolId: this.schoolId,
      studentIds: _ids,
      currentUser: this.currentUser,
    };
    this.modalsService.openRemoveStudentActivitiesModal(data);
  }
}
